import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { getApiBaseUrl } from "../../config/environment";

export const Feedback: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("bug");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setSuccess(false);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!description.trim() || !email.trim()) {
      setError("Description and Email are required.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          feedbackType: feedbackType,
          feedbackDescription: description,
          appData: {},
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      setSuccess(true);
      setTimeout(() => {
        handleClose();
        setDescription("");
        setEmail("");
        setFeedbackType("bug");
      }, 2000);
    } catch (err) {
      setError("An error occurred while submitting feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        sx={{
          position: "fixed",
          right: 0,
          top: "50%",
          transform: "translateY(-50%) rotate(180deg)",
          writingMode: "vertical-rl",
          zIndex: 1000,
          padding: "10px 5px",
          minWidth: "20px",
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
      >
        Feedback
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          {success ? (
            <Box py={4} textAlign="center">
              <Typography variant="h6" color="success.main">
                Thank you for your feedback!
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <FormControl fullWidth>
                <InputLabel id="feedback-type-label">Feedback Type</InputLabel>
                <Select
                  labelId="feedback-type-label"
                  value={feedbackType}
                  label="Feedback Type"
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <MenuItem value="bug">Bug</MenuItem>
                  <MenuItem value="feature-request">Feature Request</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Description"
                multiline
                rows={4}
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />

              <TextField
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        {!success && (
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : "Submit Feedback"}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
